import React from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import useAuth from '../auth/feature/auth';
import { HttpFactoryService } from '../common/services';
import { BACKEND_KEYS, ROUTER_LINKS } from '../common/consts/app-keys.const';

const http = new HttpFactoryService().createHttpService();

export const OAuthAuthorizePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const clientId = searchParams.get('client_id') ?? '';
  const redirectUri = searchParams.get('redirect_uri') ?? '';
  const state = searchParams.get('state') ?? '';
  const responseType = searchParams.get('response_type') ?? 'code';

  // Validate client + redirect_uri
  const { data: appInfo, isLoading, isError } = useQuery<{ name: string; description: string | null }>({
    queryKey: ['oauth-validate', clientId, redirectUri],
    queryFn: async () => {
      const res = await http.get<any>(
        `${BACKEND_KEYS.OAUTH_VALIDATE}?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`
      );
      return res as any;
    },
    enabled: !!clientId && !!redirectUri,
    retry: false
  });

  const authorizeMutation = useMutation({
    mutationFn: async () => {
      const res = await http.post<any, any>(BACKEND_KEYS.OAUTH_AUTHORIZE, {
        clientId,
        redirectUri
      });
      return res as any;
    },
    onSuccess: (data: { code: string }) => {
      const params = new URLSearchParams({ code: data.code });
      if (state) params.set('state', state);
      window.location.href = `${redirectUri}?${params.toString()}`;
    }
  });

  const handleDeny = () => {
    const params = new URLSearchParams({ error: 'access_denied' });
    if (state) params.set('state', state);
    window.location.href = `${redirectUri}?${params.toString()}`;
  };

  // Redirect to sign in if not authenticated
  if (isAuthenticated === false) {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    return (
      <div className="min-h-screen flex items-center justify-center px-[16px] py-[40px]">
        <div className="w-full max-w-[400px] bg-white dark:bg-[#1C1C1F] border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.06)] rounded-[20px] p-[32px] flex flex-col items-center gap-[16px] text-center shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
          <div className="w-[48px] h-[48px] rounded-[14px] bg-[#F5F5F6] dark:bg-[#28282B] flex items-center justify-center">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="text-[#0ea5c9]">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h1 className="font-display text-[22px] font-bold text-base-black dark:text-white">Sign in to continue</h1>
          <p className="font-body text-[14px] text-[#818185] dark:text-[#A0A0A3]">
            You need to be signed in to authorize this application.
          </p>
          <Link
            to={`${ROUTER_LINKS.SIGNIN}?returnUrl=${returnTo}`}
            className="w-full h-[48px] flex items-center justify-center bg-base-black dark:bg-white text-white dark:text-base-black rounded-[32px] font-body text-[15px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#F0F0F2] transition-all"
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  if (isAuthenticated === null || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-[40px] h-[40px] border-[3px] border-[#E5E5ED] border-t-[#0ea5c9] rounded-full animate-spin" />
      </div>
    );
  }

  if (isError || !appInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center px-[16px]">
        <div className="w-full max-w-[400px] bg-white dark:bg-[#1C1C1F] border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.06)] rounded-[20px] p-[32px] flex flex-col items-center gap-[12px] text-center">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D8210C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h1 className="font-display text-[20px] font-bold text-base-black dark:text-white">Invalid authorization request</h1>
          <p className="font-body text-[14px] text-[#818185] dark:text-[#A0A0A3]">
            The application or redirect URI is not recognized. Please contact the app developer.
          </p>
          <Link to={ROUTER_LINKS.DASHBOARD} className="mt-[4px] font-body text-[14px] text-[#0ea5c9] hover:underline">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!clientId || !redirectUri || responseType !== 'code') {
    navigate(ROUTER_LINKS.DASHBOARD);
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-[16px] py-[40px]">
      <div className="w-full max-w-[420px]">
        <div className="bg-white dark:bg-[#1C1C1F] border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.06)] rounded-[20px] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
          {/* Header */}
          <div className="px-[32px] pt-[32px] pb-[24px] border-b border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)]">
            <div className="flex items-center gap-[12px] mb-[20px]">
              <div className="w-[44px] h-[44px] rounded-[12px] bg-[#F5F5F6] dark:bg-[#28282B] flex items-center justify-center shrink-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0ea5c9" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                </svg>
              </div>
              <div>
                <p className="font-body text-[12px] text-[#818185] dark:text-[#6B6B6F] mb-[1px]">Authorization request from</p>
                <h1 className="font-display text-[18px] font-bold text-base-black dark:text-white leading-tight">
                  {appInfo.name}
                </h1>
              </div>
            </div>

            {appInfo.description && (
              <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[16px]">
                {appInfo.description}
              </p>
            )}

            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4]">
              <span className="font-semibold text-base-black dark:text-white">{appInfo.name}</span>{' '}
              is requesting access to your Standby account.
            </p>
          </div>

          {/* Permissions */}
          <div className="px-[32px] py-[20px] border-b border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)]">
            <p className="font-body text-[11px] font-semibold uppercase tracking-wider text-[#818185] dark:text-[#6B6B6F] mb-[12px]">
              This app will be able to
            </p>
            <div className="flex flex-col gap-[8px]">
              {[
                'Read and manage your queues',
                'Read and manage participants',
                'Access your team memberships',
                'Manage your webhooks'
              ].map((perm) => (
                <div key={perm} className="flex items-center gap-[10px]">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ea5c9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span className="font-body text-[13px] text-[#374151] dark:text-[#D1D1D4]">{perm}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Account info */}
          <div className="px-[32px] py-[16px] border-b border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] bg-[#F8F8F9] dark:bg-[#161618]">
            <p className="font-body text-[12px] text-[#818185] dark:text-[#6B6B6F]">
              Authorizing as <span className="font-semibold text-base-black dark:text-white">{user?.email}</span>
            </p>
          </div>

          {/* Actions */}
          <div className="px-[32px] py-[24px] flex gap-[12px]">
            <button
              type="button"
              onClick={handleDeny}
              disabled={authorizeMutation.isPending}
              className="flex-1 h-[48px] flex items-center justify-center border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] bg-[#EFEFF1] dark:bg-[#28282B] rounded-[32px] font-body text-[15px] font-medium text-base-black dark:text-white hover:bg-[#E5E5ED] dark:hover:bg-[#333336] transition-all"
            >
              Deny
            </button>
            <button
              type="button"
              onClick={() => authorizeMutation.mutate()}
              disabled={authorizeMutation.isPending}
              className="flex-1 h-[48px] flex items-center justify-center bg-[#1D1D1E] dark:bg-white text-white dark:text-base-black rounded-[32px] font-body text-[15px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#F0F0F2] disabled:opacity-50 transition-all"
            >
              {authorizeMutation.isPending ? 'Authorizing...' : 'Authorize'}
            </button>
          </div>
        </div>

        <p className="text-center font-body text-[12px] text-[#818185] dark:text-[#6B6B6F] mt-[16px]">
          You can revoke this access at any time in{' '}
          <Link to={`${ROUTER_LINKS.SETTINGS}?section=developer`} className="text-[#0ea5c9] hover:underline">
            Settings → Developer
          </Link>
        </p>
      </div>
    </div>
  );
};

export default OAuthAuthorizePage;
